import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, HostListener, inject, Input, OnInit, Output } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatCardModule } from '@angular/material/card';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MaybeAsync, RouterModule } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { Widget } from './widget';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { map } from 'rxjs';
import { InboxService } from '../../../../../core/src/lib/inbox.service';
import { GalaxyLoadingSpinnerModule } from '@vendasta/galaxy/loading-spinner';
import { KnowledgeSource } from '@vendasta/embeddings';
import { ApplicationKnowledgeComponent } from '@galaxy/ai-knowledge';
import { OpenConfirmationModalService } from '@vendasta/galaxy/confirmation-modal';
import { MatDialogModule } from '@angular/material/dialog';

@Component({
  selector: 'inbox-webchat-form',
  standalone: true,
  imports: [
    CommonModule,
    MatInputModule,
    MatCardModule,
    ReactiveFormsModule,
    FormsModule,
    MatButtonModule,
    GalaxyFormFieldModule,
    GalaxyLoadingSpinnerModule,
    TranslateModule,
    MatCheckboxModule,
    RouterModule,
    MatIconModule,
    MatButtonToggleModule,
    MatDialogModule,
    ApplicationKnowledgeComponent,
  ],
  templateUrl: './webchat-form.component.html',
  styleUrls: ['./webchat-form.component.scss'],
})
export class WebchatFormComponent implements OnInit {
  @Input() webchat: Widget;

  @Input() set disabled(value: boolean) {
    if (this.widgetForm) {
      value ? this.widgetForm.disable() : this.widgetForm.enable();
    }
  }

  @Output() formChange = new EventEmitter<Widget>();
  @Output() knowledgeSourcesChange = new EventEmitter<KnowledgeSource[]>();

  private destroyRef = inject(DestroyRef);
  widgetForm: FormGroup | undefined;
  customKnowledgeForm: FormGroup | undefined;
  textColorSelected = 'light';
  accentTextColorSelected = 'light';
  COLOR_REGEX = /^#[a-fA-F0-9]{6}$/;
  private inboxService = inject(InboxService);
  private readonly confirmationModal = inject(OpenConfirmationModalService);
  private hasUnsavedChanges = false;
  private initialSources: KnowledgeSource[] | null = null;
  isBusinessApp = this.inboxService.isBusinessApp;
  knowledgeAppId = '';

  ngOnInit(): void {
    this.widgetForm = new FormGroup({
      name: new FormControl(this.webchat?.name || ''),
      color: new FormControl(this.webchat?.color || '#555555', [
        Validators.required,
        Validators.pattern(this.COLOR_REGEX),
      ]),
      welcomeMessage: new FormControl(this.webchat?.welcomeMessage || ''),
      textColor: new FormControl(this.webchat?.textColor || 'light'),
      accentColor: new FormControl(this.webchat?.accentColor || '#1976d2', [
        Validators.required,
        Validators.pattern(this.COLOR_REGEX),
      ]),
      accentTextColor: new FormControl(this.webchat?.accentTextColor || 'light'),
    });
    this.customKnowledgeForm = new FormGroup({
      knowledgeSources: new FormControl([]),
    });
    this.textColorSelected = this.widgetForm?.get('textColor')?.value || 'light';
    this.accentTextColorSelected = this.widgetForm?.get('accentTextColor')?.value || 'light';

    this.widgetForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.formChange.emit(value);
      this.markChanges();
    });

    this.customKnowledgeForm.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      this.knowledgeSourcesChange.emit(value?.knowledgeSources);
      this.markChanges();
    });

    if (this.webchat?.widgetId) {
      this.knowledgeAppId = this.inboxService.buildAppId(this.webchat.widgetId);
    }
  }

  private knowledgeSourcesHaveChanged(): boolean {
    const currentSources: KnowledgeSource[] = this.customKnowledgeForm?.get('knowledgeSources')?.value;
    if (this.initialSources?.length !== currentSources?.length) {
      return true;
    }
    for (const source of this.initialSources || []) {
      if (!currentSources.some((s) => s.id === source.id)) {
        return true;
      }
    }
    return false;
  }

  public getContrastYIQ(color: string): string {
    const len = 2;
    const baseHex = 16;

    // remove hash from hexa string
    const hexColor = color.substring(1);

    const r = parseInt(hexColor.substr(0, len), baseHex);
    const g = parseInt(hexColor.substr(2, len), baseHex);
    const b = parseInt(hexColor.substr(4, len), baseHex);
    const yiq = (r * 299 + g * 587 + b * 114) / 1000;
    return yiq >= 128 ? 'dark' : 'light';
  }

  updateSelectedSources(sources: KnowledgeSource[] | null): void {
    if (this.initialSources === null && sources !== null) {
      this.initialSources = JSON.parse(JSON.stringify(sources));
    }
    this.customKnowledgeForm?.get('knowledgeSources')?.setValue(sources);
  }

  public setTextColor() {
    this.widgetForm
      ?.get('textColor')
      ?.setValue(this.getContrastYIQ(this.widgetForm?.get('color')?.value), { emitEvent: false });
  }

  public setAccentTextColor() {
    this.widgetForm
      ?.get('accentTextColor')
      ?.setValue(this.getContrastYIQ(this.widgetForm?.get('accentColor')?.value), { emitEvent: false });
  }

  @HostListener('window: beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasUnsavedChanges) {
      event.preventDefault();
      event.stopPropagation();
      event.returnValue = false;
    }
  }

  canDeactivate(): MaybeAsync<boolean> {
    if (this.hasUnsavedChanges) {
      return this.confirmationModal
        .openModal({
          type: 'warn',
          title: 'INBOX.UNSAVED_CHANGES.TITLE',
          message: 'INBOX.UNSAVED_CHANGES.MESSAGE',
          confirmButtonText: 'INBOX.UNSAVED_CHANGES.CONFIRM',
        })
        .pipe(map((confirmation) => !!confirmation));
    }
    return true;
  }

  private markChanges(): void {
    if (this.widgetForm?.dirty || this.customKnowledgeForm?.dirty || this.knowledgeSourcesHaveChanged()) {
      this.hasUnsavedChanges = true;
      return;
    }
    this.hasUnsavedChanges = false;
  }

  markAsSaved() {
    this.widgetForm?.markAsPristine();
    this.customKnowledgeForm?.markAsPristine();
    this.initialSources = JSON.parse(JSON.stringify(this.customKnowledgeForm?.get('knowledgeSources')?.value));
    this.hasUnsavedChanges = false;
  }
}
